/**
 * Report Visibility Utilities
 *
 * Utility functions for checking report visibility based on user permissions
 * and report visibility levels.
 */

import { UserType, UserPermission } from 'src/types/admin/accessManagement.types';
import { ReportVisibilityLevel } from 'src/types/admin/reportsConfig.types';
import * as permissionsUtils from './permissionsUtils';

/**
 * Check if a user can access a report based on its visibility level.
 *
 * Visibility hierarchy (from most restrictive to least):
 * - TECH_ADMIN: Only Tech Admins can access
 * - VERITY_ADMIN: Tech Admins OR Verity Admins can access
 * - READ_ONLY: Everyone can access
 *
 * @param visibilityLevel - The report's visibility level
 * @param userPermission - The user's permission record from Redux
 * @returns boolean - Whether the user can access the report
 */
export const canUserAccessReport = (
  visibilityLevel: ReportVisibilityLevel,
  userPermission: UserPermission | null
): boolean => {
  // No permission = no access
  if (!userPermission) return false;

  switch (visibilityLevel) {
    case UserType.TECH_ADMIN:
      // Only Tech Admins can access
      return permissionsUtils.isTechAdmin(userPermission);

    case UserType.VERITY_ADMIN:
      // Verity Admins and Tech Admins can access
      return permissionsUtils.isTechAdmin(userPermission) || permissionsUtils.isVerityAdmin(userPermission);

    case UserType.READ_ONLY:
      // Everyone can access (all authenticated users with permissions)
      return true;

    default:
      // Unknown visibility level - default to no access for safety
      return false;
  }
};

/**
 * Get a human-readable label for the visibility level.
 *
 * @param visibilityLevel - The report's visibility level
 * @returns Display label for the visibility level
 */
export const getVisibilityLabel = (visibilityLevel: ReportVisibilityLevel): string => {
  switch (visibilityLevel) {
    case UserType.TECH_ADMIN:
      return 'In Development';
    case UserType.VERITY_ADMIN:
      return 'In Review';
    case UserType.READ_ONLY:
      return 'Released';
    default:
      return 'Unknown';
  }
};

/**
 * Get the Cloudscape Badge color for the visibility level.
 *
 * Colors follow Cloudscape severity conventions:
 * - grey: In Development (TECH_ADMIN) - lowest priority/early stage
 * - blue: In Review (VERITY_ADMIN) - informational/in progress
 * - green: Released (READ_ONLY) - success/complete (not shown in UI)
 *
 * @param visibilityLevel - The report's visibility level
 * @returns Badge color for the visibility level
 */
export const getVisibilityBadgeColor = (visibilityLevel: ReportVisibilityLevel): 'grey' | 'blue' | 'green' => {
  switch (visibilityLevel) {
    case UserType.TECH_ADMIN:
      return 'grey';
    case UserType.VERITY_ADMIN:
      return 'blue';
    case UserType.READ_ONLY:
      return 'green';
    default:
      return 'grey';
  }
};

/**
 * Check if a visibility badge should be shown for the report.
 * Only shows badge for non-released reports (TECH_ADMIN, VERITY_ADMIN).
 *
 * @param visibilityLevel - The report's visibility level
 * @returns boolean - Whether to show the visibility badge
 */
export const shouldShowVisibilityBadge = (visibilityLevel: ReportVisibilityLevel): boolean => {
  return visibilityLevel !== UserType.READ_ONLY;
};

/**
 * Get the tooltip description for the visibility level.
 * Describes who can access the report at this visibility level.
 *
 * @param visibilityLevel - The report's visibility level
 * @returns Tooltip text describing access permissions
 */
export const getVisibilityTooltip = (visibilityLevel: ReportVisibilityLevel): string => {
  switch (visibilityLevel) {
    case UserType.TECH_ADMIN:
      return 'Only tech admins and developers can view this report. Reports under development are hidden from other users.';
    case UserType.VERITY_ADMIN:
      return 'Verity admins and tech admins can view this report. Reports in review are being tested before full release.';
    case UserType.READ_ONLY:
      return 'All users can view this report. This is a fully released report.';
    default:
      return 'Report visibility is restricted.';
  }
};
